/**
 * Custom hooks for Ports API
 *
 * Provides hooks for searching ports for autocomplete dropdowns
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

// ============================================================================
// TYPES
// ============================================================================

export interface PortSearchItem {
  port_id: string;
  un_locode: string;
  port_code: string;
  port_name: string;
  country_code: string;
  display_name: string; // "Port Name, CC (CODE)"
  lat: number | null;
  lon: number | null;
  is_major_port: boolean;
}

export interface PortSearchResponse {
  ports: PortSearchItem[];
  total: number;
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const PORTS_QUERY_KEYS = {
  all: ["ports"] as const,
  search: (query: string, countryCode?: string) =>
    [...PORTS_QUERY_KEYS.all, "search", query, countryCode] as const,
  byId: (id: string) => [...PORTS_QUERY_KEYS.all, id] as const,
};

// ============================================================================
// SEARCH PORTS HOOK
// ============================================================================

/**
 * Hook to search ports for autocomplete
 *
 * @param query - Search term (min 2 chars)
 * @param countryCode - Optional country code filter (ISO 3166-1 alpha-2)
 * @param enabled - Whether the query should be enabled
 *
 * Returns ports formatted as "Port Name, CC (CODE)" for display
 * Major ports are returned first
 */
export function usePorts(
  query: string,
  countryCode?: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: PORTS_QUERY_KEYS.search(query, countryCode),
    queryFn: async ({ signal }): Promise<PortSearchResponse> => {
      const params: Record<string, string | number> = {
        limit: 50,
      };
      if (query) params.q = query;
      if (countryCode) params.country_code = countryCode;

      return await apiGet<PortSearchResponse>("/ports", { params, signal });
    },
    enabled: enabled && query.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 10 * 60 * 1000, // 10 minutes - ports don't change often
  });
}

// ============================================================================
// GET PORT BY ID HOOK
// ============================================================================

/**
 * Hook to fetch a single port by ID
 *
 * @param portId - Port UUID
 * @param enabled - Whether the query should be enabled
 */
export function usePortById(portId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: PORTS_QUERY_KEYS.byId(portId || ""),
    queryFn: async (): Promise<PortSearchItem> => {
      if (!portId) throw new Error("No port ID provided");
      return await apiGet<PortSearchItem>(`/ports/${portId}`);
    },
    enabled: enabled && !!portId,
    staleTime: Infinity, // Ports are static reference data
  });
}

// ============================================================================
// MAJOR PORTS HOOK (for initial dropdown)
// ============================================================================

/**
 * Hook to fetch major ports for initial dropdown display
 *
 * Returns only major ports for faster initial loading
 */
export function useMajorPorts(enabled: boolean = true) {
  return useQuery({
    queryKey: [...PORTS_QUERY_KEYS.all, "major"],
    queryFn: async (): Promise<PortSearchResponse> => {
      return await apiGet<PortSearchResponse>("/ports", {
        params: { major_only: true, limit: 100 },
      });
    },
    enabled,
    staleTime: Infinity, // Major ports are static
  });
}
